import classNames from 'classnames';
import type { ComponentProps } from 'react';
import React, { forwardRef, useLayoutEffect, useState } from 'react';
import type { DistributedPick } from 'type-fest';
import FloatingCard from './FloatingCard';
import HoverView from './HoverView';
import * as styles from './LargeTooltip.module.scss';

const calculateMaxWidth = (text: string) => {
  const words = text.trim().split(/\s+/).length;
  if (words >= 5 && words <= 20) {
    return 200;
  }
  if (words > 20) {
    return 300;
  }

  return undefined;
};

interface LargeTooltipContentProps extends ComponentProps<typeof FloatingCard> {
  children: React.ReactNode;
  autoMaxWidthDisabled?: boolean;
  noPadding?: boolean;
}

const LargeTooltipContent = forwardRef<
  HTMLDivElement,
  LargeTooltipContentProps
>(({ children, autoMaxWidthDisabled, noPadding, ...rest }, ref) => {
  const [maxWidth, setMaxWidth] = useState<number | undefined>(undefined);
  const [containerElement, setContainerElement] =
    useState<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!containerElement || autoMaxWidthDisabled) {
      return undefined;
    }

    const mutationObserver = new MutationObserver(() => {
      setMaxWidth(calculateMaxWidth(containerElement.textContent ?? ''));
    });

    setMaxWidth(calculateMaxWidth(containerElement.textContent ?? ''));
    mutationObserver.observe(containerElement, {
      subtree: true,
      childList: true,
    });

    return () => {
      mutationObserver.disconnect();
    };
  }, [containerElement, autoMaxWidthDisabled]);

  return (
    <FloatingCard ref={ref} {...rest}>
      <div
        ref={setContainerElement}
        className={classNames(styles.inner, {
          [styles.withPadding]: !noPadding,
        })}
        style={{ maxWidth }}
      >
        {children}
      </div>
    </FloatingCard>
  );
});

type LargeTooltipProps = {
  content?: React.ReactNode;
  offset?: number;
  autoMaxWidthDisabled?: boolean;
  noPadding?: boolean;
  children: React.ReactNode;
} & DistributedPick<
  ComponentProps<typeof HoverView>,
  'placement' | 'flipBehavior' | 'offset' | 'open' | 'onOpenChange'
>;

const LargeTooltip = ({
  content,
  children,
  autoMaxWidthDisabled = false,
  noPadding,
  placement = 'top',
  ...rest
}: LargeTooltipProps) => {
  if (!content) {
    return <>{children}</>;
  }

  return (
    <HoverView
      hoverGroupId="tooltip"
      view={
        <LargeTooltipContent
          noPadding={noPadding}
          autoMaxWidthDisabled={autoMaxWidthDisabled}
        >
          {content}
        </LargeTooltipContent>
      }
      placement={placement}
      {...rest}
    >
      {children}
    </HoverView>
  );
};

interface LargeTooltipHeadingProps {
  children: React.ReactNode;
}

const LargeTooltipHeading = ({ children }: LargeTooltipHeadingProps) => {
  return <h2 className={styles.heading}>{children}</h2>;
};

LargeTooltip.Heading = LargeTooltipHeading;

export default LargeTooltip;
